import { useEffect, useMemo, useState, type FormEvent } from 'react'
import {
  ArrowLeft,
  Bug,
  HelpCircle,
  Lightbulb,
  Loader2,
  Mail,
  Send,
  type LucideIcon,
} from 'lucide-react'
import { Page } from '@/components/Page'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { FormField } from '@/components/FormField'
import { Modal } from '@/components/Modal'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/hooks/useToast'
import { useAuth } from '@/auth/useAuth'
import { ApiError } from '@/lib/api'
import { useNavigate } from 'react-router-dom'
import { Inbox } from 'lucide-react'
import {
  useMyContactQueries,
  useSendContactSales,
} from '@/features/contactSales/queries'
import type {
  AudienceScope,
  BugSeverity,
  ContactCategory,
  ContactSalesPayload,
  FeaturePriority,
  OrganizationType,
} from '@/features/contactSales/types'

const ORG_TYPES: OrganizationType[] = ['Corporation', 'LLC', 'Non-Profit', 'Individual', 'Other']
const SEVERITIES: { value: BugSeverity; label: string }[] = [
  { value: 'low', label: 'Low - minor inconvenience' },
  { value: 'medium', label: 'Medium - noticeable but workable' },
  { value: 'high', label: 'High - blocks important work' },
  { value: 'critical', label: 'Critical - production down or data at risk' },
]
const PRIORITIES: { value: FeaturePriority; label: string }[] = [
  { value: 'nice-to-have', label: 'Nice to have' },
  { value: 'important', label: 'Important' },
  { value: 'urgent', label: 'Urgent' },
]
const AUDIENCES: { value: AudienceScope; label: string }[] = [
  { value: 'just-me', label: 'Just me' },
  { value: 'my-team', label: 'My team' },
  { value: 'many-companies', label: 'Many companies would benefit' },
]

interface CategoryMeta {
  key: ContactCategory
  title: string
  blurb: string
  cta: string
  icon: LucideIcon
}

const CATEGORIES: CategoryMeta[] = [
  {
    key: 'sales',
    title: 'Talk to Sales',
    blurb: 'Pricing, enterprise plans, custom contracts, or volume discounts.',
    cta: 'Contact sales',
    icon: Mail,
  },
  {
    key: 'bug',
    title: 'Report an Issue',
    blurb: 'Something is broken or behaving unexpectedly. Tell us what went wrong.',
    cta: 'Report a bug',
    icon: Bug,
  },
  {
    key: 'feature',
    title: 'Request a Feature',
    blurb: 'Ask us to build something custom that would make your work easier.',
    cta: 'Request a feature',
    icon: Lightbulb,
  },
  {
    key: 'support',
    title: 'Get Help',
    blurb: 'A "how do I…" question that the User Guide does not cover yet.',
    cta: 'Ask for help',
    icon: HelpCircle,
  },
]

function emptyPayload(category: ContactCategory): ContactSalesPayload {
  return {
    category,
    name: '',
    email: '',
    subject: '',
    description: '',
    type: category === 'sales' ? 'Corporation' : undefined,
    severity: category === 'bug' ? 'medium' : undefined,
    priority: category === 'feature' ? 'important' : undefined,
    audience: category === 'feature' ? 'my-team' : undefined,
  }
}

export function ContactSalesPage() {
  const { toast } = useToast()
  const { user } = useAuth()
  const navigate = useNavigate()
  const sendM = useSendContactSales()
  const myQueriesQ = useMyContactQueries()
  const myCount = myQueriesQ.data?.total ?? 0
  const [category, setCategory] = useState<ContactCategory | null>(null)
  const [form, setForm] = useState<ContactSalesPayload>(() => emptyPayload('sales'))
  const [successOpen, setSuccessOpen] = useState(false)

  const browserInfo = useMemo(() => summarizeBrowser(), [])

  const meta = category ? CATEGORIES.find((c) => c.key === category) ?? null : null

  useEffect(() => {
    if (!category) return
    setForm({
      ...emptyPayload(category),
      email: user?.email ?? '',
      browserInfo: category === 'bug' ? browserInfo : undefined,
    })
  }, [category, user?.email, browserInfo])

  const set = <K extends keyof ContactSalesPayload>(key: K, value: ContactSalesPayload[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }))

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!category) return

    const trimmed: ContactSalesPayload = {
      ...form,
      category,
      name: form.name.trim(),
      email: form.email.trim(),
      subject: form.subject.trim(),
      description: form.description.trim(),
      company: form.company?.trim() || undefined,
      teamSize: form.teamSize?.trim() || undefined,
      pageAffected: form.pageAffected?.trim() || undefined,
      stepsToReproduce: form.stepsToReproduce?.trim() || undefined,
      expectedBehavior: form.expectedBehavior?.trim() || undefined,
      actualBehavior: form.actualBehavior?.trim() || undefined,
      browserInfo: form.browserInfo?.trim() || undefined,
      useCase: form.useCase?.trim() || undefined,
    }

    try {
      await sendM.mutateAsync(trimmed)
      setSuccessOpen(true)
      setForm(emptyPayload(category))
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Unable to send your message',
        description: messageFor(err, 'Connection failed.'),
      })
    }
  }

  if (!category || !meta) {
    return (
      <Page
        title="Contact us"
        description="Pick the option that best fits what you need. We will get back to you by email."
        width="wide"
        actions={
          myCount > 0 ? (
            <Button
              variant="ghost"
              onClick={() => navigate('/contact-sales/queries')}
            >
              <Inbox className="size-4" />
              Your queries ({myCount})
            </Button>
          ) : null
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {CATEGORIES.map((c) => {
            const Icon = c.icon
            return (
              <Button
                key={c.key}
                type="button"
                variant="outline"
                onClick={() => setCategory(c.key)}
                className="group flex h-auto min-h-[160px] flex-col items-start gap-3 whitespace-normal !rounded-lg border p-5 text-left hover:border-primary [&_svg]:size-5"
              >
                <span className="flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <Icon />
                </span>
                <span className="text-base font-semibold text-foreground">{c.title}</span>
                <span className="text-sm font-normal text-muted-foreground">{c.blurb}</span>
                <span className="mt-auto text-sm font-medium text-primary group-hover:underline">
                  {c.cta} →
                </span>
              </Button>
            )
          })}
        </div>
      </Page>
    )
  }

  return (
    <Page
      title="Contact us"
      description={meta.blurb}
      width="wide"
      actions={
        <Button variant="ghost" onClick={() => setCategory(null)}>
          <ArrowLeft className="size-4" />
          Back to options
        </Button>
      }
    >
      <Tabs value={category} onValueChange={(v) => setCategory(v as ContactCategory)}>
        <TabsList className="h-auto flex-wrap justify-start gap-1 bg-transparent p-0">
          {CATEGORIES.map((c) => {
            const Icon = c.icon
            return (
              <TabsTrigger
                key={c.key}
                value={c.key}
                className="gap-2 border data-[state=active]:border-primary data-[state=active]:bg-primary/10 data-[state=active]:text-primary"
              >
                <Icon className="size-4" />
                {c.title}
              </TabsTrigger>
            )
          })}
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <CardTitle>{meta.title}</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <FormField
                label="Full Name"
                required
                value={form.name}
                onChange={(e) => set('name', e.currentTarget.value)}
                autoComplete="name"
              />
              <FormField
                label="Email"
                required
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.currentTarget.value)}
                autoComplete="email"
              />
            </div>

            {category === 'sales' ? <SalesFields form={form} set={set} /> : null}
            {category === 'bug' ? <BugFields form={form} set={set} /> : null}
            {category === 'feature' ? <FeatureFields form={form} set={set} /> : null}
            {category === 'support' ? <SupportFields form={form} set={set} /> : null}

            <FormField
              label="Subject"
              required
              value={form.subject}
              onChange={(e) => set('subject', e.currentTarget.value)}
              placeholder={subjectPlaceholder(category)}
            />

            <FormField
              label={descriptionLabel(category)}
              required
              control={
                <Textarea
                  required
                  value={form.description}
                  onChange={(e) => set('description', e.currentTarget.value)}
                  placeholder={descriptionPlaceholder(category)}
                  className="min-h-[140px]"
                />
              }
            />

            <Button type="submit" className="w-full" disabled={sendM.isPending}>
              {sendM.isPending ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Send className="size-4" />
              )}
              {sendM.isPending ? 'Sending…' : 'Send message'}
            </Button>
          </form>
        </CardContent>
      </Card>

      <Modal
        open={successOpen}
        onOpenChange={setSuccessOpen}
        title="Message sent"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setSuccessOpen(false)
                setCategory(null)
              }}
            >
              Close
            </Button>
            <Button
              onClick={() => {
                setSuccessOpen(false)
                navigate('/contact-sales/queries')
              }}
            >
              View your queries
            </Button>
          </>
        }
      >
        <p className="text-sm">
          Thanks - Bokapsys has received your message. You can track its status and reply
          to follow-ups under <span className="font-medium">Your queries</span>.
        </p>
      </Modal>
    </Page>
  )
}

interface SectionProps {
  form: ContactSalesPayload
  set: <K extends keyof ContactSalesPayload>(key: K, value: ContactSalesPayload[K]) => void
}

function SalesFields({ form, set }: SectionProps) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <FormField
        label="Company Name"
        required
        value={form.company ?? ''}
        onChange={(e) => set('company', e.currentTarget.value)}
        autoComplete="organization"
      />
      <FormField
        label="Approximate Team Size"
        value={form.teamSize ?? ''}
        onChange={(e) => set('teamSize', e.currentTarget.value)}
        placeholder="e.g. 1-10, 50, 500+"
      />
      <FormField
        label="Organization Type"
        control={
          <Select
            value={form.type ?? 'Corporation'}
            onValueChange={(v) => set('type', v as OrganizationType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ORG_TYPES.map((t) => (
                <SelectItem key={t} value={t}>
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />
    </div>
  )
}

function BugFields({ form, set }: SectionProps) {
  return (
    <div className="space-y-4">
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Severity"
          required
          control={
            <Select
              value={form.severity ?? 'medium'}
              onValueChange={(v) => set('severity', v as BugSeverity)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SEVERITIES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <FormField
          label="Page or Feature Affected"
          value={form.pageAffected ?? ''}
          onChange={(e) => set('pageAffected', e.currentTarget.value)}
          placeholder="e.g. Invoices, Reconciliation, Reports"
        />
      </div>
      <FormField
        label="Steps to Reproduce"
        control={
          <Textarea
            value={form.stepsToReproduce ?? ''}
            onChange={(e) => set('stepsToReproduce', e.currentTarget.value)}
            placeholder={'1. Open …\n2. Click …\n3. Observe …'}
            className="min-h-[100px]"
          />
        }
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Expected Behaviour"
          control={
            <Textarea
              value={form.expectedBehavior ?? ''}
              onChange={(e) => set('expectedBehavior', e.currentTarget.value)}
              className="min-h-[80px]"
            />
          }
        />
        <FormField
          label="Actual Behaviour"
          control={
            <Textarea
              value={form.actualBehavior ?? ''}
              onChange={(e) => set('actualBehavior', e.currentTarget.value)}
              className="min-h-[80px]"
            />
          }
        />
      </div>
      <FormField
        label="Browser / OS"
        hint="Auto-filled so we can reproduce the issue on the same setup. Edit if you saw it on a different device."
        value={form.browserInfo ?? ''}
        onChange={(e) => set('browserInfo', e.currentTarget.value)}
      />
    </div>
  )
}

function FeatureFields({ form, set }: SectionProps) {
  return (
    <div className="space-y-4">
      <FormField
        label="What are you trying to do?"
        hint="Describe the use case in your own words - what would this feature unlock for you?"
        control={
          <Textarea
            value={form.useCase ?? ''}
            onChange={(e) => set('useCase', e.currentTarget.value)}
            className="min-h-[100px]"
          />
        }
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField
          label="Who would benefit?"
          control={
            <Select
              value={form.audience ?? 'my-team'}
              onValueChange={(v) => set('audience', v as AudienceScope)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AUDIENCES.map((a) => (
                  <SelectItem key={a.value} value={a.value}>
                    {a.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
        <FormField
          label="Priority"
          control={
            <Select
              value={form.priority ?? 'important'}
              onValueChange={(v) => set('priority', v as FeaturePriority)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.value} value={p.value}>
                    {p.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          }
        />
      </div>
    </div>
  )
}

function SupportFields({ form, set }: SectionProps) {
  return (
    <FormField
      label="Page or Feature"
      value={form.pageAffected ?? ''}
      onChange={(e) => set('pageAffected', e.currentTarget.value)}
      placeholder="Where in the app are you stuck?"
    />
  )
}

function subjectPlaceholder(category: ContactCategory): string {
  switch (category) {
    case 'sales':
      return 'Pricing for a 200-person finance team'
    case 'bug':
      return 'Reconciliation totals do not match for August'
    case 'feature':
      return 'Bulk import for journal entries'
    case 'support':
      return 'How do I close a fiscal year?'
  }
}

function descriptionLabel(category: ContactCategory): string {
  switch (category) {
    case 'sales':
      return 'How can we help?'
    case 'bug':
      return 'Additional details'
    case 'feature':
      return 'Anything else we should know?'
    case 'support':
      return 'Your question'
  }
}

function descriptionPlaceholder(category: ContactCategory): string {
  switch (category) {
    case 'sales':
      return 'Describe your volume, feature requirements, or integration needs…'
    case 'bug':
      return 'Anything else that helps us reproduce or understand the issue.'
    case 'feature':
      return 'Examples, comparable products, screenshots if useful.'
    case 'support':
      return 'Tell us what you have already tried.'
  }
}

function summarizeBrowser(): string {
  if (typeof navigator === 'undefined') return ''
  const ua = navigator.userAgent
  const lang = navigator.language || ''

  let browser = 'Unknown browser'
  const edge = ua.match(/Edg\/(\d+)/)
  const chrome = ua.match(/Chrome\/(\d+)/)
  const firefox = ua.match(/Firefox\/(\d+)/)
  const safari = ua.match(/Version\/(\d+).*Safari/)
  if (edge) browser = `Edge ${edge[1]}`
  else if (firefox) browser = `Firefox ${firefox[1]}`
  else if (chrome) browser = `Chrome ${chrome[1]}`
  else if (safari) browser = `Safari ${safari[1]}`

  let os = 'Unknown OS'
  if (/Windows NT 10/.test(ua)) os = 'Windows 10/11'
  else if (/Windows/.test(ua)) os = 'Windows'
  else if (/Mac OS X ([\d_]+)/.test(ua)) os = `macOS ${(ua.match(/Mac OS X ([\d_]+)/) ?? [])[1]?.replace(/_/g, '.') ?? ''}`.trim()
  else if (/Android (\d+)/.test(ua)) os = `Android ${(ua.match(/Android (\d+)/) ?? [])[1] ?? ''}`.trim()
  else if (/iPhone|iPad/.test(ua)) os = 'iOS'
  else if (/Linux/.test(ua)) os = 'Linux'

  return [browser, os, lang].filter(Boolean).join(' · ')
}

function messageFor(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}
